/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

/**
 * Page-scoped stylesheets, mirroring the <Seo> Head links (which handle the
 * initial SSR load). Gatsby's Head API applies head changes only AFTER the
 * new page has rendered, so on client-side navigation the destination page
 * painted unstyled for a few frames (the "scattered banner" flash) before
 * its stylesheet appeared. Worst case: /case-studies list -> case-study
 * detail, where the SAME sheet was removed and re-added across the
 * transition. These copies are injected outside Gatsby's managed head at
 * navigation start and never removed; each sheet's selectors are
 * page-scoped by design, so a resident sheet cannot affect other pages.
 */
const PAGE_CSS = [
  { test: /^\/case-studies/, href: "/assets/css/cs-pages.css" },
  {
    test: /^\/(healthcare|construction|wearable-iot|education|travel|entertainment|sports-fitness)/,
    href: "/assets/css/ind-pages.css",
  },
  { test: /^\/press-release/, href: "/assets/css/press-pages.css" },
]

const residentCss = new Set()

const ensurePageCss = pathname => {
  PAGE_CSS.forEach(({ test, href }) => {
    if (!test.test(pathname) || residentCss.has(href)) return
    residentCss.add(href)
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = href
    link.setAttribute("data-page-css", "resident")
    document.head.appendChild(link)
  })
}

export const onClientEntry = () => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }

  // Warm every page-scoped sheet once the main thread is idle, so the
  // first client-side navigation to one of those pages finds the file in
  // the HTTP cache and the resident link applies before the page paints.
  const warm = () => {
    PAGE_CSS.forEach(({ href }) => {
      const link = document.createElement("link")
      link.rel = "prefetch"
      link.as = "style"
      link.href = href
      document.head.appendChild(link)
    })
  }
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 4000 })
  } else {
    window.setTimeout(warm, 2500)
  }
}

export const onPreRouteUpdate = ({ location }) => {
  // Navigation has started but the new page has not rendered yet: make the
  // destination's page-scoped CSS resident NOW so the first paint of the
  // new route is already styled.
  if (location) ensurePageCss(location.pathname)
}

const resetScrollPosition = () => {
  document.documentElement.style.scrollBehavior = "auto"
  document.body.style.scrollBehavior = "auto"

  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

// Jump to the element a URL hash points at. `scrollIntoView` honours the
// `scroll-margin-top` set on anchored headings, so the heading clears the
// fixed header instead of hiding underneath it.
const scrollToHash = hash => {
  if (!hash) return false
  let id
  try {
    id = decodeURIComponent(hash.slice(1))
  } catch (e) {
    id = hash.slice(1)
  }
  const el = document.getElementById(id)
  if (!el) return false

  document.documentElement.style.scrollBehavior = "auto"
  document.body.style.scrollBehavior = "auto"
  el.scrollIntoView()
  return true
}

export const onRouteUpdate = ({ location } = {}) => {
  // A hash means the visitor is targeting a specific element: an in-page
  // "Table of contents" link, or a deep link straight to a section. The
  // scroll resets below would cancel that jump, so scroll to the anchor
  // instead. Repeat once layout has settled, because images and fonts
  // finishing after the first attempt shift the target's offset.
  if (location && location.hash) {
    scrollToHash(location.hash)
    requestAnimationFrame(() => scrollToHash(location.hash))
    setTimeout(() => scrollToHash(location.hash), 120)
    setTimeout(() => {
      scrollToHash(location.hash)
      document.documentElement.style.scrollBehavior = ""
      document.body.style.scrollBehavior = ""
    }, 400)
    return
  }

  resetScrollPosition()
  requestAnimationFrame(() => resetScrollPosition())
  setTimeout(() => resetScrollPosition(), 60)

  setTimeout(() => {
    document.documentElement.style.scrollBehavior = ""
    document.body.style.scrollBehavior = ""
  }, 100)
}

export const shouldUpdateScroll = ({ routerProps } = {}) => {
  // Anchor targets are handled in onRouteUpdate: returning early here keeps
  // the pre-paint reset from cancelling the jump before it happens.
  if (routerProps && routerProps.location && routerProps.location.hash) {
    return false
  }

  // Runs synchronously before the browser paints the new route, unlike
  // onRouteUpdate (post-paint). Resetting here removes the frames where
  // the new page was visible at the old page's scroll offset — the brief
  // "scattered then snaps back" flash in the banner on every client-side
  // navigation. The onRouteUpdate resets stay as a backstop.
  resetScrollPosition()
  return false
}
